import { useEffect, useState, type ReactNode } from 'react';
import { usePaymentsDialog } from '../payments/paymentsDialogStore';
import { ServerClock, Wordmark, useMoneyFormat, useSessionContext } from '@adiox/ui';
import { AlertsPanel } from '../alerts/AlertsPanel';
import { MailboxPanel } from '../mailbox/MailboxPanel';
import { ChallengePanel } from '../challenge/ChallengePanel';
import { lossRoom } from '../challenge/model';
import { useChallenge } from '../challenge/useChallenge';
import { HistoryPanel } from '../history/HistoryPanel';
import { MarketWatch } from '../market/MarketWatch';
import { NotificationsPanel } from '../notifications/NotificationsPanel';
import { useNotifications } from '../notifications/notificationStore';
import { AccountBar } from '../shell/AccountBar';
import { ConnectionStatus } from '../shell/ConnectionStatus';
import { useStream } from '../stream/stream';
import { OrderTicket } from '../trading/OrderTicket';
import { TradePanel } from '../trading/TradePanel';
import { useActiveLogin } from '../trading/useTrading';
import { useWorkspace } from '../workspace/workspaceStore';
import { MobileChart } from './MobileChart';
import { useInstall } from '../shell/install';
import { Sheet } from './Sheet';

type Tab = 'quotes' | 'chart' | 'trade' | 'history' | 'more';
type MoreView = 'notifications' | 'mailbox' | 'alerts' | 'challenge' | null;

const TABS: { id: Tab; label: string; glyph: string }[] = [
  { id: 'quotes', label: 'Quotes', glyph: '⇅' },
  { id: 'chart', label: 'Chart', glyph: '📈' },
  { id: 'trade', label: 'Trade', glyph: '▤' },
  { id: 'history', label: 'History', glyph: '⟲' },
  { id: 'more', label: 'More', glyph: '⋯' },
];

/** Equity and floating profit in the header (and the loss left on a challenge): tap for the whole account. */
function AccountSummary({ onOpen }: { onOpen: () => void }) {
  const login = useActiveLogin();
  const account = useStream((s) => (login === undefined ? undefined : s.accounts[login]));
  const challenge = useChallenge().data;
  const fmt = useMoneyFormat();
  if (!account?.loaded) return null;
  const f = account.figures;
  const room = challenge ? lossRoom(challenge.current.progress) : null;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="num flex min-w-0 flex-col items-end rounded-md px-2 py-0.5 text-right hover:bg-panel2"
      aria-label={`Account #${login}: equity ${fmt(f.equity)} ${account.currency}. Show the whole account`}
      data-testid="mobile-account"
    >
      <span className="text-sm font-semibold">
        {fmt(f.equity)} <span className="text-xs text-muted">{account.currency}</span>
      </span>
      <span className="text-xs">
        <span className={f.floating > 0 ? 'text-up' : f.floating < 0 ? 'text-down' : 'text-muted'}>
          {f.floating > 0 ? '+' : ''}
          {fmt(f.floating)}
        </span>
        {room && <span className="text-muted"> · {fmt(room.daily)} left today</span>}
      </span>
    </button>
  );
}

function MoreList({
  onPick,
  onSettings,
  onGuide,
}: {
  onPick: (view: MoreView) => void;
  onSettings: () => void;
  onGuide: () => void;
}) {
  const install = useInstall();
  const { tokens } = useSessionContext();
  const unread = useNotifications((s) => s.unread);
  const challenge = useChallenge().data;
  const item =
    'flex w-full items-center justify-between border-b border-line px-4 py-3.5 text-left hover:bg-panel2';
  return (
    <nav aria-label="More" className="flex flex-col">
      <button type="button" className={item} onClick={() => onPick('notifications')}>
        Notifications
        {unread > 0 && (
          <span className="rounded-full bg-accent px-2 text-xs text-accent-fg">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      <button type="button" className={item} onClick={() => onPick('mailbox')}>
        Mailbox
      </button>
      <button type="button" className={item} onClick={() => onPick('alerts')}>
        Price alerts
      </button>
      {challenge && (
        <button type="button" className={item} onClick={() => onPick('challenge')}>
          Challenge
        </button>
      )}
      <button type="button" className={item} onClick={() => usePaymentsDialog.getState().show()}>
        Deposit and withdrawal
      </button>
      <button type="button" className={item} onClick={onSettings}>
        Settings
      </button>
      <button type="button" className={item} onClick={onGuide}>
        Welcome guide
      </button>
      {install.canInstall && (
        <button type="button" className={item} onClick={() => void install.install()}>
          Install as an app
        </button>
      )}
      {install.iosHint && (
        <p className="border-b border-line px-4 py-3 text-sm text-muted">
          To open the terminal like an app: tap Share, then Add to Home Screen.
        </p>
      )}
      <button type="button" className={`${item} text-down`} onClick={() => void tokens.logout()}>
        Sign out
      </button>
    </nav>
  );
}

function Back({ title, onBack, children }: { title: string; onBack: () => void; children: ReactNode }) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-line px-2 py-1.5">
        <button type="button" onClick={onBack} className="rounded px-2 py-1 text-sm hover:bg-panel2">
          ‹ More
        </button>
        <h2 className="text-sm font-semibold">{title}</h2>
      </div>
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  );
}

/**
 * The terminal on a phone or an upright tablet: one screen at a time with tabs at the bottom (Quotes, Chart, Trade,
 * History, More), the account in the header, and the order ticket as a full-screen sheet.
 */
export function MobileWorkspace({
  onOpenSettings,
  onOpenGuide,
}: {
  onOpenSettings: () => void;
  onOpenGuide: () => void;
}) {
  const [tab, setTab] = useState<Tab>('quotes');
  const [more, setMore] = useState<MoreView>(null);
  const [ticket, setTicket] = useState(false);
  const [account, setAccount] = useState(false);
  const unread = useNotifications((s) => s.unread);

  // Every "new order" and "open chart" in the terminal (Market Watch's menu, the chart's menu, F9) lands here.
  useEffect(() => {
    const workspace = useWorkspace.getState();
    workspace.setActions({
      orderTicket: () => setTicket(true),
      newChart: (symbol) => {
        if (symbol) useWorkspace.getState().select(symbol);
        setTab('chart');
      },
    });
    return () => useWorkspace.getState().setActions(null);
  }, []);

  const screen = (() => {
    switch (tab) {
      case 'quotes':
        return <MarketWatch />;
      case 'chart':
        return <MobileChart />;
      case 'trade':
        return <TradePanel />;
      case 'history':
        return <HistoryPanel />;
      case 'more':
        if (more === 'notifications')
          return (
            <Back title="Notifications" onBack={() => setMore(null)}>
              <NotificationsPanel />
            </Back>
          );
        if (more === 'mailbox')
          return (
            <Back title="Mailbox" onBack={() => setMore(null)}>
              <MailboxPanel />
            </Back>
          );
        if (more === 'alerts')
          return (
            <Back title="Price alerts" onBack={() => setMore(null)}>
              <AlertsPanel />
            </Back>
          );
        if (more === 'challenge')
          return (
            <Back title="Challenge" onBack={() => setMore(null)}>
              <ChallengePanel />
            </Back>
          );
        return <MoreList onPick={setMore} onSettings={onOpenSettings} onGuide={onOpenGuide} />;
    }
  })();

  return (
    <div className="flex h-[100dvh] flex-col bg-bg" data-testid="mobile-workspace">
      <header className="flex items-center gap-2 border-b border-line bg-panel px-3 py-1.5 pt-[max(0.375rem,env(safe-area-inset-top))]">
        <Wordmark className="h-5 shrink-0" />
        <div className="min-w-0 flex-1 truncate text-xs">
          <ConnectionStatus />
        </div>
        <AccountSummary onOpen={() => setAccount(true)} />
        <button
          type="button"
          onClick={() => setTicket(true)}
          className="shrink-0 rounded-md bg-accent px-3 py-1.5 text-sm font-semibold text-accent-fg"
        >
          New order
        </button>
      </header>

      <main
        className="min-h-0 flex-1 overflow-hidden bg-panel"
        aria-label={TABS.find((t) => t.id === tab)!.label}
      >
        {screen}
      </main>

      <nav
        aria-label="Sections"
        className="grid grid-cols-5 border-t border-line bg-panel pb-[env(safe-area-inset-bottom)]"
      >
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            aria-current={tab === t.id ? 'page' : undefined}
            onClick={() => {
              setTab(t.id);
              if (t.id === 'more') setMore(null);
            }}
            className={`relative flex min-h-14 flex-col items-center justify-center gap-0.5 text-xs ${
              tab === t.id ? 'text-accent' : 'text-muted'
            }`}
          >
            <span aria-hidden="true" className="text-lg leading-none">
              {t.glyph}
            </span>
            {t.label}
            {t.id === 'more' && unread > 0 && (
              <span
                className="absolute right-[22%] top-1.5 h-2 w-2 rounded-full bg-accent"
                aria-hidden="true"
              />
            )}
          </button>
        ))}
      </nav>

      <Sheet open={ticket} title="New order" onClose={() => setTicket(false)}>
        <OrderTicket />
      </Sheet>
      <Sheet open={account} title="Account" onClose={() => setAccount(false)}>
        <div className="flex flex-col gap-4 p-4">
          <AccountBar wrap />
          <ServerClock />
          <div className="flex gap-2">
            <button
              type="button"
              className="flex-1 rounded-md border border-line px-3 py-2 text-sm hover:border-accent"
              onClick={() => {
                setAccount(false);
                onOpenSettings();
              }}
            >
              Settings
            </button>
          </div>
        </div>
      </Sheet>
    </div>
  );
}
